// Mantém o app sempre na versão deployada, sem o usuário precisar limpar cache
// e SEM impacto negativo (nunca recarrega por cima de uma digitação/modal).
//
// Contexto do problema: um SPA, uma vez carregado numa aba, continua rodando o
// JS em memória e NÃO rebusca index.html/bundles até um reload de página
// inteira. Se um deploy acontece com a aba aberta, o usuário fica preso na
// versão antiga (foi a causa do "Erro ao atualizar recebimento" que só ocorria
// em aba antiga aberta durante um deploy).
//
// Estratégia (sem service worker, sem mudar o build): comparar o hash do
// main.js no /asset-manifest.json (muda a cada deploy) com o carregado nesta
// aba. Ao detectar nova versão:
//   1) avisa a UI (banner com botão "Atualizar agora");
//   2) aplica sozinho só em momentos 100% seguros:
//        - quando a aba vai para segundo plano (reload invisível); ou
//        - ocioso por bastante tempo, sem campo focado e sem modal aberto.
// Também há uma rede de segurança para ChunkLoadError (aba antiga tentando
// carregar um chunk que sumiu no deploy).

const MANIFEST_URL = '/asset-manifest.json';
const POLL_MS = 3 * 60 * 1000;  // verifica nova versão a cada 3 min
const IDLE_MS = 90 * 1000;      // "ocioso" = 90s sem interação (fallback seguro)
const CHECK_MS = 15 * 1000;     // frequência da checagem de ociosidade

let baselineHash = null;    // hash do main.js carregado nesta aba
let novaVersao = false;     // deploy novo já detectado?
let jaAvisou = false;       // callback de UI já disparado?
let ultimaAtividade = Date.now();
let onNovaVersao = null;    // callback da UI

async function buscarHashMain() {
    try {
        const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.files?.['main.js'] || null;
    } catch {
        return null; // rede instável: tenta de novo no próximo ciclo
    }
}

// Telas que não podem ser recarregadas por baixo do usuário (ex.: a contagem da
// Revisão de Estoque) seguram o reload automático enquanto estão em uso. É um
// contador: vários bloqueios simultâneos não atrapalham uns aos outros.
let bloqueios = 0;

/**
 * Segura o reload automático. Devolve a função que libera — chame no cleanup
 * do efeito. O banner "nova versão" continua aparecendo e o botão "Atualizar
 * agora" continua funcionando: o que fica bloqueado é só o reload sozinho.
 */
export function bloquearAtualizacaoAutomatica() {
    bloqueios += 1;
    let liberado = false;
    return () => {
        if (liberado) return;
        liberado = true;
        bloqueios = Math.max(0, bloqueios - 1);
    };
}

// Usuário está no meio de uma ação? (não recarregar por cima)
function usuarioOcupado() {
    const el = document.activeElement;
    if (el) {
        const tag = el.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
        if (el.isContentEditable) return true;
    }
    // Modais do Chakra usam role="dialog"/"alertdialog"
    if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return true;
    return false;
}

function marcarNovaVersao() {
    if (novaVersao) return;
    novaVersao = true;
    if (!jaAvisou && typeof onNovaVersao === 'function') {
        jaAvisou = true;
        try { onNovaVersao(); } catch { /* UI opcional */ }
    }
}

// Reload seguro: só quando há nova versão, o usuário NÃO está no meio de algo
// e está ocioso há bastante tempo. Ocioso (>=90s sem interação) garante que
// não há digitação/edição em andamento -> zero risco de perder dados. Vale
// tanto para aba visível (fallback) quanto ao ocultar a aba (reload invisível).
function talvezRecarregar() {
    if (!novaVersao) return;
    if (bloqueios > 0) return;
    if (usuarioOcupado()) return;
    if (Date.now() - ultimaAtividade > IDLE_MS) {
        window.location.reload();
    }
}

// Rede de segurança: aba antiga tentando carregar um chunk que sumiu no deploy.
//
// Recarregar resolve o caso normal (o reload traz o index.html novo). Mas se o
// chunk continuar faltando, cada recarga refaz o mesmo erro e a página entra em
// ciclo de recarregamento. No Safari do iPhone isso vira "Um problema ocorreu
// repetidamente" e a aba morre. Por isso o limite abaixo: a guarda desiste
// depois de MAX_TENTATIVAS e deixa a página parada, que é ruim mas é
// diagnosticável, em vez de um loop infinito.
const CHAVE_CHUNK = 'chunk-reload';
const MAX_TENTATIVAS = 2;
const JANELA_TENTATIVAS_MS = 60 * 1000; // tentativas contam dentro desta janela
const DEBOUNCE_RELOAD_MS = 10 * 1000;   // dois erros juntos não viram duas recargas
const PRAZO_APP_SAUDAVEL_MS = 30 * 1000;

/**
 * Tentativas já feitas nesta aba, ou null se o sessionStorage não estiver
 * disponível (Safari com storage bloqueado, por exemplo).
 *
 * A diferença importa: o contador é a ÚNICA coisa que sobrevive a um reload.
 * Sem ele não há como saber que já tentamos, e recarregar viraria justamente o
 * loop que esta guarda existe para impedir. Por isso, sem storage, não
 * recarregamos.
 */
function lerTentativas() {
    try {
        const bruto = sessionStorage.getItem(CHAVE_CHUNK);
        if (!bruto) return { n: 0, ts: 0 };
        const dado = JSON.parse(bruto);
        return { n: Number(dado?.n) || 0, ts: Number(dado?.ts) || 0 };
    } catch {
        return null;
    }
}

/** Grava as tentativas. false = não deu para persistir. */
function gravarTentativas(n, ts) {
    try {
        sessionStorage.setItem(CHAVE_CHUNK, JSON.stringify({ n, ts }));
        return true;
    } catch {
        return false;
    }
}

function instalarGuardaChunk() {
    const recarregarComLimite = () => {
        const agora = Date.now();
        const tentativas = lerTentativas();

        if (tentativas === null) {
            console.error('[atualizacao] Chunk faltando, mas sem sessionStorage para contar as tentativas. Nao vou recarregar (evita loop). Recarregue a pagina manualmente.');
            return;
        }

        let { n } = tentativas;
        const { ts } = tentativas;

        // Fora da janela: o problema de antes já passou, começa a contagem do zero.
        if (agora - ts > JANELA_TENTATIVAS_MS) {
            n = 0;
        } else if (agora - ts < DEBOUNCE_RELOAD_MS) {
            return; // acabou de recarregar, deixa a página terminar de subir
        }

        if (n >= MAX_TENTATIVAS) {
            console.error(
                '[atualizacao] Chunk continua faltando apos ' + MAX_TENTATIVAS +
                ' recargas. Parando para nao entrar em loop. Recarregue a pagina manualmente.'
            );
            return;
        }

        if (!gravarTentativas(n + 1, agora)) {
            console.error('[atualizacao] Nao consegui registrar a tentativa de recarga. Nao vou recarregar (evita loop).');
            return;
        }

        window.location.reload();
    };

    const ehErroDeChunk = (msg) =>
        /Loading chunk [\w-]+ failed|ChunkLoadError|Loading CSS chunk/i.test(msg || '');

    window.addEventListener('error', (e) => {
        if (ehErroDeChunk(e?.message)) recarregarComLimite();
    });
    window.addEventListener('unhandledrejection', (e) => {
        if (ehErroDeChunk(e?.reason?.message)) recarregarComLimite();
    });

    // App ficou de pé por um tempo sem erro de chunk: zera o contador para que um
    // problema novo daqui a uma hora tenha as tentativas dele de volta.
    setTimeout(() => {
        try { sessionStorage.removeItem(CHAVE_CHUNK); } catch { /* noop */ }
    }, PRAZO_APP_SAUDAVEL_MS);
}

/** Inicia o detector. `cb` é chamado uma vez quando uma nova versão é detectada. */
export function iniciarDetectorDeVersao(cb) {
    onNovaVersao = cb || null;

    if (window.__atualizacaoAutoOn) {
        // Já rodando: se já detectou antes do componente montar, avisa agora.
        if (novaVersao && !jaAvisou && typeof onNovaVersao === 'function') {
            jaAvisou = true;
            try { onNovaVersao(); } catch { /* noop */ }
        }
        return;
    }
    window.__atualizacaoAutoOn = true;

    instalarGuardaChunk();

    ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'].forEach((ev) =>
        window.addEventListener(ev, () => { ultimaAtividade = Date.now(); }, { passive: true, capture: true })
    );

    // Baseline: versão carregada nesta aba
    buscarHashMain().then((h) => { baselineHash = h; });

    // Poll por nova versão (só com a aba visível, para não gastar rede à toa)
    setInterval(async () => {
        if (document.visibilityState !== 'visible') return;
        const atual = await buscarHashMain();
        if (!atual) return;
        if (!baselineHash) baselineHash = atual;          // estabelece baseline se faltou no load
        else if (atual !== baselineHash) marcarNovaVersao();
        talvezRecarregar();
    }, POLL_MS);

    // Fallback de ociosidade (aba sempre em foco, mas parada)
    setInterval(talvezRecarregar, CHECK_MS);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            // Aba indo pro segundo plano: se ocioso, reload invisível.
            talvezRecarregar();
        } else {
            // Usuário voltou pra aba: conta como atividade (nunca recarrega na cara dele).
            ultimaAtividade = Date.now();
        }
    });
}

/** Aplica a atualização imediatamente (botão "Atualizar agora"). */
export function aplicarAtualizacaoAgora() {
    window.location.reload();
}
